import { useRef, useEffect } from "react"
import { Check } from "lucide-react"
import { useUpdateThread } from "@/hooks/useChat"
import { Input } from "@/components/ui/input"
import { useQueryClient } from "@tanstack/react-query"

interface EditThreadProps {
  threadId: string
  currentName: string
  isEditing: boolean
  onEditStart: () => void
  onEditEnd: () => void
}

export function EditThread({
  threadId,
  currentName: _currentName,
  isEditing,
  onEditStart: _onEditStart,
  onEditEnd,
}: EditThreadProps) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)

  const updateThreadMutation = useUpdateThread({
    onSuccess: () => {
      // Refresh threads list
      queryClient.invalidateQueries({ queryKey: ["threads"] })
      onEditEnd()
    },
    onError: (error) => {
      console.error("Failed to update thread:", error)
    },
  })

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = (newName: string) => {
    if (newName.trim()) {
      updateThreadMutation.mutate({
        threadId,
        data: { name: newName.trim() },
      })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, currentValue: string) => {
    if (e.key === "Enter") {
      handleSave(currentValue)
    } else if (e.key === "Escape") {
      onEditEnd()
    }
  }

  return {
    inputRef,
    updateThreadMutation,
    handleSave,
    handleKeyDown,
  }
}

interface EditThreadInputProps {
  threadId: string
  currentName: string
  isEditing: boolean
  onEditStart: () => void
  onEditEnd: () => void
  onNameChange: (name: string) => void
  editingName: string
}

export function EditThreadInput({
  threadId,
  currentName,
  isEditing,
  onEditStart: _onEditStart,
  onEditEnd,
  onNameChange,
  editingName,
}: EditThreadInputProps) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)

  const updateThreadMutation = useUpdateThread({
    onSuccess: () => {
      // Refresh threads list
      queryClient.invalidateQueries({ queryKey: ["threads"] })
      onEditEnd()
    },
    onError: (error) => {
      console.error("Failed to update thread:", error)
    },
  })

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    if (editingName.trim()) {
      updateThreadMutation.mutate({
        threadId,
        data: { name: editingName.trim() },
      })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave()
    } else if (e.key === "Escape") {
      onEditEnd()
    }
  }

  if (!isEditing) {
    return <h3 className="truncate text-sm font-medium text-gray-900 dark:text-white">{currentName}</h3>
  }

  return (
    <Input
      ref={inputRef}
      value={editingName}
      onChange={(e) => onNameChange(e.target.value)}
      onKeyDown={handleKeyDown}
      className="h-6 text-sm font-medium"
      disabled={updateThreadMutation.isPending}
      suffix={
        <button
          onClick={handleSave}
          disabled={updateThreadMutation.isPending || !editingName.trim()}
          className="flex h-4 w-4 items-center justify-center rounded hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
        >
          <Check className="h-3 w-3 text-green-600" />
        </button>
      }
    />
  )
}
